import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import SuperAdminNav from "../components/superAdminNav";
import Time from "../components/time";
import {
  ArrowLeft,
  Building2,
  Database,
  UserRound,
  ShieldCheck,
  LockKeyhole,
  Mail,
  Eye,
  EyeOff,
  RefreshCw,
  Save,
  CheckCircle2,
  AlertCircle,
  Users,
  Power,
  Search,
  KeyRound,
  ChevronRight,
  Server,
  Info,
  X,
  Pencil,
  Sparkles,
} from "lucide-react";

// ============================================================
// CONFIG
// ============================================================

const API = (process.env.REACT_APP_API_URL || "https://supremeadmin.nissi-india.com").replace(
  /\/+$/,
  ""
);

const api = axios.create({
  baseURL: `${API}/api`,
  withCredentials: true,
});

const emptyForm = {
  adminFullName: "",
  adminUsername: "",
  adminPassword: "",
  confirmPassword: "",
  email: "",
};

const inputClass =
  "w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10 disabled:bg-slate-100 disabled:text-slate-400";
const labelClass = "mb-1.5 block text-xs font-semibold text-slate-700";
const primaryButton =
  "inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-emerald-700 px-5 text-xs font-bold text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50";
const secondaryButton =
  "inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50";

const getError = (error) =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  "Something went wrong";

const isActive = (value) => value === true || value === 1;
const isReady = (value) => String(value || "").toUpperCase() === "READY";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-IN");
};

// Strong random password generator
function generateStrongPassword(length = 12) {
  const chars =
    "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*";
  let password = "";
  const array = new Uint32Array(length);
  crypto.getRandomValues(array);
  for (let i = 0; i < length; i++) {
    password += chars[array[i] % chars.length];
  }
  return password;
}

// ============================================================
// SMALL COMPONENTS
// ============================================================

function SectionHeader({ icon: Icon, title, description, right }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-emerald-100 bg-emerald-50/60 px-5 py-4">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
          <Icon size={18} />
        </div>
        <div>
          <h2 className="text-sm font-bold text-slate-800">{title}</h2>
          {description && (
            <p className="mt-0.5 text-[11px] text-slate-500">{description}</p>
          )}
        </div>
      </div>
      {right}
    </div>
  );
}

function FormField({ label, required, hint, children }) {
  return (
    <div>
      <label className={labelClass}>
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>
      {children}
      {hint && <p className="mt-1 text-[11px] text-slate-400">{hint}</p>}
    </div>
  );
}

function StatusBadge({ value }) {
  const active = isActive(value);
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold ${
        active ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          active ? "bg-emerald-500" : "bg-red-400"
        }`}
      />
      {active ? "Active" : "Inactive"}
    </span>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function Tenant_Bootstrap() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [tenants, setTenants] = useState([]);
  const [selectedId, setSelectedId] = useState(id || "");
  const [tenantDetails, setTenantDetails] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [search, setSearch] = useState("");
  const [editingUsername, setEditingUsername] = useState("");

  const selectedTenant = useMemo(
    () =>
      tenants.find((tenant) => String(tenant.TenantId) === String(selectedId)) ||
      null,
    [tenants, selectedId]
  );

  const ready = isReady(selectedTenant?.ProvisioningStatus);
  const selectedDatabase =
    selectedTenant?.DatabaseName || tenantDetails?.DatabaseName || "";

  const filteredAdmins = useMemo(
    () =>
      admins.filter((admin) =>
        [admin.username, admin.full_name, admin.email].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(search.trim().toLowerCase())
        )
      ),
    [admins, search]
  );

  const totalActive = useMemo(
    () => admins.filter((admin) => isActive(admin.is_active)).length,
    [admins]
  );

  // ============================================================
  // FETCH TENANTS
  // ============================================================

  const loadTenants = useCallback(async () => {
    setLoadingTenants(true);
    try {
      const response = await api.get("/tenant/mysql/tenants");
      const data = response.data;

      if (data?.success === false || data?.ok === false) {
        throw new Error(data.error || data.message || "Failed to fetch tenants");
      }

      setTenants(Array.isArray(data.data) ? data.data : []);
    } catch (error) {
      console.error("loadTenants error:", error);
      Swal.fire({
        icon: "error",
        title: "Tenant Loading Failed",
        text: getError(error),
        confirmButtonColor: "#047857",
      });
      setTenants([]);
    } finally {
      setLoadingTenants(false);
    }
  }, []);

  // ============================================================
  // FETCH TENANT DETAILS
  // ============================================================

  const loadTenantDetails = useCallback(async (tenantId) => {
    if (!tenantId) {
      setTenantDetails(null);
      setAdmins([]);
      return;
    }

    setLoadingDetails(true);
    setTenantDetails(null);
    setAdmins([]);

    try {
      const response = await api.get(
        `/tenant/mysql/tenant/${encodeURIComponent(tenantId)}`
      );
      const data = response.data;

      if (data?.success === false || data?.ok === false) {
        throw new Error(
          data.error || data.message || "Failed to fetch tenant details"
        );
      }

      setTenantDetails(data.tenant || null);
      setAdmins(Array.isArray(data.admins) ? data.admins : []);
    } catch (error) {
      console.error("loadTenantDetails error:", error);
      Swal.fire({
        icon: "error",
        title: "Unable to Load Accounts",
        text: getError(error),
        confirmButtonColor: "#047857",
      });
    } finally {
      setLoadingDetails(false);
    }
  }, []);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  useEffect(() => {
    loadTenantDetails(selectedId);
  }, [selectedId, loadTenantDetails]);

  // ============================================================
  // HANDLERS
  // ============================================================

  const handleTenantChange = (event) => {
    setSelectedId(event.target.value);
    setForm(emptyForm);
    setEditingUsername("");
    setSearch("");
    setShowPassword(false);
    setShowConfirm(false);
  };

  const handleInput = (event) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: value }));
  };

  const clearForm = () => {
    setForm(emptyForm);
    setEditingUsername("");
    setShowPassword(false);
    setShowConfirm(false);
  };

  const handleEdit = (admin) => {
    setEditingUsername(admin.username);
    setForm({
      adminFullName: admin.full_name || "",
      adminUsername: admin.username || "",
      adminPassword: "",
      confirmPassword: "",
      email: admin.email || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Auto generate password
  const handleGeneratePassword = () => {
    const newPassword = generateStrongPassword(12);
    setForm((prev) => ({
      ...prev,
      adminPassword: newPassword,
      confirmPassword: newPassword,
    }));
    setShowPassword(true);
    setShowConfirm(true);
    Swal.fire({
      icon: "success",
      title: "Password Generated",
      text: "A strong password has been generated and filled in both fields.",
      timer: 1800,
      showConfirmButton: false,
    });
  };

  // ============================================================
  // SAVE SUPER ADMIN
  // ============================================================

  const handleSave = async (event) => {
    event.preventDefault();

    if (!selectedId)
      return Swal.fire("Validation", "Please select a tenant", "warning");
    if (!ready)
      return Swal.fire(
        "Validation",
        "Selected tenant MASTER database is not ready",
        "warning"
      );
    if (!form.adminFullName.trim())
      return Swal.fire(
        "Validation",
        "Please enter the Super Admin full name",
        "warning"
      );
    if (!form.adminUsername.trim())
      return Swal.fire("Validation", "Please enter the username", "warning");
    if (form.adminUsername.trim().length > 100)
      return Swal.fire(
        "Validation",
        "Username cannot exceed 100 characters",
        "warning"
      );
    if (form.adminFullName.trim().length > 200)
      return Swal.fire(
        "Validation",
        "Full name cannot exceed 200 characters",
        "warning"
      );
    if (form.adminPassword.length < 8 || form.adminPassword.length > 1024)
      return Swal.fire(
        "Validation",
        "Password must contain 8 to 1024 characters",
        "warning"
      );
    if (form.adminPassword !== form.confirmPassword)
      return Swal.fire("Validation", "Passwords do not match", "warning");
    if (
      form.email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())
    )
      return Swal.fire(
        "Validation",
        "Please enter a valid email address",
        "warning"
      );

    const existing = admins.find(
      (admin) => admin.username === form.adminUsername.trim()
    );

    if (existing) {
      const confirm = await Swal.fire({
        title: "Update Existing Super Admin?",
        text: `The username "${existing.username}" already exists. Saving will update the password, full name and email, and activate this account.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, Update",
        confirmButtonColor: "#047857",
        cancelButtonColor: "#64748b",
      });
      if (!confirm.isConfirmed) return;
    }

    setSaving(true);

    try {
      const response = await api.post("/tenant/mysql/bootstrap", {
        tenantId: Number(selectedId),
        tenantCode: selectedTenant?.TenantCode,
        adminFullName: form.adminFullName.trim(),
        adminUsername: form.adminUsername.trim(),
        adminPassword: form.adminPassword,
        email: form.email.trim() || null,
      });

      const data = response.data;

      if (data?.success === false || data?.ok === false) {
        throw new Error(data.error || data.message || "Unable to save Super Admin");
      }

      clearForm();
      await loadTenantDetails(selectedId);

      await Swal.fire({
        icon: "success",
        title: existing ? "Super Admin Updated" : "Super Admin Created",
        text: data.message || "Tenant Super Admin credentials saved successfully",
        confirmButtonColor: "#047857",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: getError(error),
        confirmButtonColor: "#047857",
      });
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // ACTIVATE / DEACTIVATE
  // ============================================================

  const handleToggleStatus = async (admin) => {
    const nextStatus = !isActive(admin.is_active);

    const confirm = await Swal.fire({
      title: nextStatus ? "Activate Super Admin?" : "Deactivate Super Admin?",
      text: `${nextStatus ? "Activate" : "Deactivate"} "${admin.username}"?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: nextStatus ? "Activate" : "Deactivate",
      confirmButtonColor: nextStatus ? "#047857" : "#dc2626",
      cancelButtonColor: "#64748b",
    });

    if (!confirm.isConfirmed) return;

    setUpdatingId(admin.super_admin_id);

    try {
      const response = await api.patch(
        `/tenant/mysql/tenant/${encodeURIComponent(
          selectedId
        )}/admins/${encodeURIComponent(admin.super_admin_id)}/status`,
        { isActive: nextStatus }
      );

      if (response.data?.success === false || response.data?.ok === false) {
        throw new Error(
          response.data.error || response.data.message || "Status update failed"
        );
      }

      await loadTenantDetails(selectedId);
      Swal.fire({
        icon: "success",
        title: "Updated",
        text: response.data.message || "Status updated successfully",
        confirmButtonColor: "#047857",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: getError(error),
        confirmButtonColor: "#047857",
      });
    } finally {
      setUpdatingId(null);
    }
  };

  // ============================================================
  // UI
  // ============================================================

  return (
    <div className="min-h-screen bg-[#f5f8f6]">
      <div className="flex min-h-screen flex-col md:flex-row">
        <SuperAdminNav />

        <main className="min-w-0 flex-1 px-4 py-5 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-[1500px]">
            {/* BREADCRUMB */}
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-slate-500">
                <button
                  type="button"
                  onClick={() => navigate("/superAdmin/admin_Menu")}
                  className="hover:text-emerald-700"
                >
                  Supreme Admin
                </button>
                <ChevronRight size={13} />
                <span>Tenant Management</span>
                <ChevronRight size={13} />
                <span className="font-bold text-emerald-700">
                  Tenant Bootstrap
                </span>
              </div>
              <Time />
            </div>

            {/* PAGE TITLE */}
            <div className="mb-5 overflow-hidden rounded-xl border border-emerald-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-4 border-l-4 border-emerald-600 px-5 py-5">
                <div className="flex items-start gap-3">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                    <ShieldCheck size={24} />
                  </div>
                  <div>
                    <p className="mb-1 text-[11px] font-bold uppercase tracking-wider text-emerald-700">
                      Infra V5 / Tenant Management
                    </p>
                    <h1 className="text-xl font-bold tracking-tight text-slate-900 md:text-2xl">
                      Tenant Super Admin Bootstrap
                    </h1>
                    <p className="mt-1 text-xs text-slate-500">
                      Create and manage Super Admin credentials for an existing
                      tenant MASTER database.
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={loadTenants}
                    disabled={loadingTenants}
                    className={secondaryButton}
                  >
                    <RefreshCw
                      size={14}
                      className={loadingTenants ? "animate-spin" : ""}
                    />{" "}
                    Refresh
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/superAdmin/admin_Menu")}
                    className={secondaryButton}
                  >
                    <ArrowLeft size={14} /> Back
                  </button>
                </div>
              </div>
            </div>

            {/* STATS */}
            <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {[
                {
                  title: "Registered Tenants",
                  value: tenants.length,
                  icon: Building2,
                  color: "bg-emerald-50 text-emerald-700",
                },
                {
                  title: "MASTER Database",
                  value: selectedDatabase ? "Selected" : "Not Selected",
                  icon: Database,
                  color: "bg-teal-50 text-teal-700",
                },
                {
                  title: "Super Admin Accounts",
                  value: admins.length,
                  icon: Users,
                  color: "bg-blue-50 text-blue-700",
                },
                {
                  title: "Active Accounts",
                  value: totalActive,
                  icon: ShieldCheck,
                  color: "bg-violet-50 text-violet-700",
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <div>
                    <p className="text-[11px] font-semibold text-slate-500">
                      {item.title}
                    </p>
                    <p className="mt-1 text-xl font-bold text-slate-900">
                      {item.value}
                    </p>
                  </div>
                  <div
                    className={`flex h-11 w-11 items-center justify-center rounded-xl ${item.color}`}
                  >
                    <item.icon size={20} />
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
              {/* LEFT SIDE */}
              <div className="space-y-5 xl:col-span-5">
                {/* TENANT SELECTION */}
                <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  <SectionHeader
                    icon={Building2}
                    title="Tenant Selection"
                    description="Choose an existing registered tenant"
                  />
                  <div className="space-y-4 p-5">
                    <FormField label="Tenant Name" required>
                      <select
                        value={selectedId}
                        onChange={handleTenantChange}
                        disabled={loadingTenants || saving}
                        className={inputClass}
                      >
                        <option value="">
                          {loadingTenants
                            ? "Loading tenants..."
                            : "-- Select Tenant --"}
                        </option>
                        {tenants.map((tenant) => (
                          <option key={tenant.TenantId} value={tenant.TenantId}>
                            {tenant.Name} ({tenant.TenantCode})
                          </option>
                        ))}
                      </select>
                    </FormField>

                    {selectedTenant ? (
                      <div className="overflow-hidden rounded-xl border border-emerald-100 bg-emerald-50/30">
                        <div className="flex items-center justify-between gap-2 border-b border-emerald-100 px-4 py-3">
                          <div className="flex items-center gap-2 text-xs font-bold text-emerald-800">
                            <Server size={15} /> MASTER Database Details
                          </div>
                          <span
                            className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${
                              ready
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-amber-100 text-amber-700"
                            }`}
                          >
                            {selectedTenant.ProvisioningStatus || "UNKNOWN"}
                          </span>
                        </div>
                        <div className="space-y-3 p-4">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <p className="text-[11px] text-slate-500">
                                Tenant Code
                              </p>
                              <p className="mt-1 text-sm font-bold text-slate-800">
                                {selectedTenant.TenantCode}
                              </p>
                            </div>
                            <div>
                              <p className="text-[11px] text-slate-500">
                                Tenant ID
                              </p>
                              <p className="mt-1 text-sm font-bold text-slate-800">
                                {selectedTenant.TenantId}
                              </p>
                            </div>
                          </div>
                          <div>
                            <p className="mb-1 text-[11px] text-slate-500">
                              MASTER Database Name
                            </p>
                            <div className="break-all rounded-lg border border-emerald-100 bg-white px-3 py-2.5 font-mono text-xs font-semibold text-emerald-800">
                              {selectedDatabase || "Not configured"}
                            </div>
                          </div>
                          {!ready && (
                            <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-700">
                              <AlertCircle size={15} className="shrink-0" />
                              The MASTER database must be READY before creating
                              credentials.
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center">
                        <Database size={28} className="mb-2 text-slate-300" />
                        <p className="text-xs text-slate-500">
                          Select a tenant to view its MASTER database.
                        </p>
                      </div>
                    )}
                  </div>
                </section>

                {/* CREDENTIAL FORM */}
                <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  <SectionHeader
                    icon={UserRound}
                    title={
                      editingUsername
                        ? "Update Super Admin"
                        : "Create Super Admin"
                    }
                    description="Credentials will be saved in the selected MASTER database"
                    right={
                      editingUsername && (
                        <button
                          type="button"
                          onClick={clearForm}
                          className="text-xs font-bold text-emerald-700 hover:underline"
                        >
                          <X size={13} className="mr-1 inline" /> Cancel Edit
                        </button>
                      )
                    }
                  />

                  <form onSubmit={handleSave} className="space-y-4 p-5">
                    <FormField label="Super Admin Full Name" required>
                      <div className="relative">
                        <UserRound
                          size={15}
                          className="absolute left-3 top-3 text-slate-400"
                        />
                        <input
                          name="adminFullName"
                          value={form.adminFullName}
                          onChange={handleInput}
                          disabled={!ready || saving}
                          maxLength={200}
                          placeholder="Enter full name"
                          className={`${inputClass} pl-9`}
                        />
                      </div>
                    </FormField>

                    <FormField label="Login Username" required>
                      <div className="relative">
                        <ShieldCheck
                          size={15}
                          className="absolute left-3 top-3 text-slate-400"
                        />
                        <input
                          name="adminUsername"
                          value={form.adminUsername}
                          onChange={handleInput}
                          disabled={!ready || saving || Boolean(editingUsername)}
                          maxLength={100}
                          autoComplete="off"
                          placeholder="Enter username"
                          className={`${inputClass} pl-9`}
                        />
                      </div>
                    </FormField>

                    <FormField label="Email Address">
                      <div className="relative">
                        <Mail
                          size={15}
                          className="absolute left-3 top-3 text-slate-400"
                        />
                        <input
                          name="email"
                          type="email"
                          value={form.email}
                          onChange={handleInput}
                          disabled={!ready || saving}
                          maxLength={256}
                          placeholder="admin@company.com"
                          className={`${inputClass} pl-9`}
                        />
                      </div>
                    </FormField>

                    {/* Password + Generate */}
                    <div className="flex items-end justify-between gap-2">
                      <FormField
                        label={editingUsername ? "New Password" : "Password"}
                        required
                        hint="Minimum 8 characters. You can type manually or generate one."
                      >
                        <div className="relative">
                          <LockKeyhole
                            size={15}
                            className="absolute left-3 top-3 text-slate-400"
                          />
                          <input
                            name="adminPassword"
                            type={showPassword ? "text" : "password"}
                            value={form.adminPassword}
                            onChange={handleInput}
                            disabled={!ready || saving}
                            autoComplete="new-password"
                            placeholder="Enter or generate password"
                            className={`${inputClass} pl-9 pr-10`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            className="absolute right-3 top-2.5 text-slate-400 hover:text-emerald-700"
                          >
                            {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                          </button>
                        </div>
                      </FormField>

                      <button
                        type="button"
                        onClick={handleGeneratePassword}
                        disabled={!ready || saving}
                        className="mb-1 inline-flex h-10 shrink-0 items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 text-xs font-bold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                      >
                        <Sparkles size={14} />
                        Generate
                      </button>
                    </div>

                    <FormField label="Confirm Password" required>
                      <div className="relative">
                        <KeyRound
                          size={15}
                          className="absolute left-3 top-3 text-slate-400"
                        />
                        <input
                          name="confirmPassword"
                          type={showConfirm ? "text" : "password"}
                          value={form.confirmPassword}
                          onChange={handleInput}
                          disabled={!ready || saving}
                          autoComplete="new-password"
                          placeholder="Confirm password"
                          className={`${inputClass} pl-9 pr-10`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirm((v) => !v)}
                          className="absolute right-3 top-2.5 text-slate-400 hover:text-emerald-700"
                        >
                          {showConfirm ? <EyeOff size={17} /> : <Eye size={17} />}
                        </button>
                      </div>
                    </FormField>

                    <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 pt-4">
                      <button
                        type="button"
                        onClick={clearForm}
                        disabled={saving}
                        className={`${secondaryButton} flex-1`}
                      >
                        Clear
                      </button>
                      <button
                        type="submit"
                        disabled={!ready || loadingDetails || saving}
                        className={`${primaryButton} flex-[2]`}
                      >
                        {saving ? (
                          <>
                            <RefreshCw size={15} className="animate-spin" />{" "}
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save size={15} />{" "}
                            {editingUsername
                              ? "Update Super Admin"
                              : "Save Super Admin"}
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </section>
              </div>

              {/* RIGHT SIDE */}
              <div className="space-y-5 xl:col-span-7">
                {/* EXISTING ACCOUNTS */}
                <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  <SectionHeader
                    icon={Users}
                    title="Existing Super Admin Accounts"
                    description="View and manage tenant MASTER database credentials"
                    right={
                      <button
                        type="button"
                        onClick={() => loadTenantDetails(selectedId)}
                        disabled={!selectedId || loadingDetails}
                        className={secondaryButton}
                      >
                        <RefreshCw
                          size={13}
                          className={loadingDetails ? "animate-spin" : ""}
                        />{" "}
                        Refresh
                      </button>
                    }
                  />

                  <div className="p-5">
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                      <div className="relative w-full max-w-sm">
                        <Search
                          size={15}
                          className="absolute left-3 top-3 text-slate-400"
                        />
                        <input
                          value={search}
                          onChange={(event) => setSearch(event.target.value)}
                          disabled={!selectedId}
                          placeholder="Search administrator..."
                          className={`${inputClass} pl-9`}
                        />
                      </div>
                      <span className="rounded-lg bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700">
                        {filteredAdmins.length} Records
                      </span>
                    </div>

                    <div className="overflow-x-auto rounded-xl border border-slate-200">
                      <table className="w-full min-w-[700px] border-collapse text-left text-xs">
                        <thead className="bg-emerald-50 text-emerald-900">
                          <tr>
                            <th className="px-4 py-3 font-bold">S.No</th>
                            <th className="px-4 py-3 font-bold">
                              Administrator
                            </th>
                            <th className="px-4 py-3 font-bold">Username</th>
                            <th className="px-4 py-3 font-bold">Status</th>
                            <th className="px-4 py-3 font-bold">Created</th>
                            <th className="px-4 py-3 text-center font-bold">
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                          {loadingDetails ? (
                            <tr>
                              <td
                                colSpan={6}
                                className="px-4 py-12 text-center text-slate-400"
                              >
                                <RefreshCw
                                  size={20}
                                  className="mx-auto mb-2 animate-spin text-emerald-600"
                                />{" "}
                                Loading accounts...
                              </td>
                            </tr>
                          ) : !selectedId ? (
                            <tr>
                              <td
                                colSpan={6}
                                className="px-4 py-12 text-center text-slate-400"
                              >
                                Please select a tenant first.
                              </td>
                            </tr>
                          ) : filteredAdmins.length === 0 ? (
                            <tr>
                              <td
                                colSpan={6}
                                className="px-4 py-12 text-center text-slate-400"
                              >
                                No Super Admin accounts found.
                              </td>
                            </tr>
                          ) : (
                            filteredAdmins.map((admin, index) => (
                              <tr
                                key={admin.super_admin_id}
                                className="transition hover:bg-emerald-50/30"
                              >
                                <td className="px-4 py-3 text-slate-500">
                                  {index + 1}
                                </td>
                                <td className="px-4 py-3">
                                  <p className="font-bold text-slate-800">
                                    {admin.full_name || "-"}
                                  </p>
                                  <p className="mt-0.5 text-[11px] text-slate-400">
                                    {admin.email || "No email"}
                                  </p>
                                </td>
                                <td className="px-4 py-3 font-semibold text-slate-700">
                                  {admin.username}
                                </td>
                                <td className="px-4 py-3">
                                  <StatusBadge value={admin.is_active} />
                                </td>
                                <td className="px-4 py-3 text-slate-500">
                                  {formatDate(admin.created_at)}
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center justify-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => handleEdit(admin)}
                                      title="Edit administrator"
                                      className="rounded-lg border border-emerald-100 bg-emerald-50 p-2 text-emerald-700 hover:bg-emerald-100"
                                    >
                                      <Pencil size={14} />
                                    </button>
                                    <button
                                      type="button"
                                      disabled={
                                        updatingId === admin.super_admin_id
                                      }
                                      onClick={() => handleToggleStatus(admin)}
                                      title={
                                        isActive(admin.is_active)
                                          ? "Deactivate"
                                          : "Activate"
                                      }
                                      className={`rounded-lg border p-2 disabled:opacity-50 ${
                                        isActive(admin.is_active)
                                          ? "border-red-100 bg-red-50 text-red-600 hover:bg-red-100"
                                          : "border-emerald-100 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                      }`}
                                    >
                                      {updatingId === admin.super_admin_id ? (
                                        <RefreshCw
                                          size={14}
                                          className="animate-spin"
                                        />
                                      ) : (
                                        <Power size={14} />
                                      )}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </section>

                {/* INFORMATION */}
                <section className="overflow-hidden rounded-xl border border-emerald-100 bg-white shadow-sm">
                  <SectionHeader
                    icon={Info}
                    title="Tenant Bootstrap Information"
                    description="Infra V5 credential creation flow"
                  />
                  <div className="space-y-3 p-5 text-xs text-slate-600">
                    {[
                      "Select an existing tenant from the Supreme Admin registry.",
                      "The MASTER database is resolved automatically using the tenant ID.",
                      "Create or update the tenant Super Admin login credentials.",
                      "The backend encrypts the password using AES-256-CBC to match the existing Infra V5 login.",
                      "Credentials are saved in dbo.tbl_SuperAdmin_Credential.",
                      "No INVENTORY, PAYROLL or TALLY database changes are performed.",
                    ].map((item, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <CheckCircle2
                          size={15}
                          className="mt-0.5 shrink-0 text-emerald-600"
                        />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </section>

                {/* SELECTED DB FOOTER */}
                {selectedTenant && (
                  <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                    <Database
                      size={19}
                      className="mt-0.5 shrink-0 text-emerald-700"
                    />
                    <div>
                      <p className="text-xs font-bold text-emerald-900">
                        Selected MASTER Database
                      </p>
                      <p className="mt-1 break-all font-mono text-xs text-emerald-700">
                        {selectedDatabase}
                      </p>
                      <p className="mt-2 text-[11px] text-emerald-700">
                        All credential operations are scoped to this tenant's
                        MASTER database.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 pt-4 text-[11px] text-slate-400">
              <span>Infra V5 Supreme Admin / Tenant Bootstrap</span>
              <span>Tenant MASTER Credential Management</span>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}